import {Alert, type ButtonProps} from 'platform/components';

import {isNil} from 'ramda';

import {type SellerOfferAlternatives} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {type OfferAlternative} from '../types/OfferAlternative';
import {getAlternativeLabel} from '../utils/getAlternativeLabel';

const t = (key: string, fallback: string, options: Record<string, string | number> = {}) =>
  i18n.t(key, {defaultValue: fallback, ...options});

interface CustomerChoiceBannerProps {
  businessCaseId: string;
  offerAlternatives: SellerOfferAlternatives;
  isApplying: boolean;
  onApply: (alternative: OfferAlternative, onApplied: VoidFunction) => void;
}

/**
 * What the customer chose in the online offer. "Continue to checkout" applies the choice and,
 * once that succeeds, opens checkout: only an applied alternative is the deal vehicle that
 * checkout, contract and reservation use.
 */
export function CustomerChoiceBanner(props: CustomerChoiceBannerProps) {
  const navigate = useNavigate();
  const {customerChoice} = props.offerAlternatives;
  const chosen = props.offerAlternatives.alternatives.find(
    (alternative) => alternative.id === customerChoice?.alternativeId
  );

  if (isNil(customerChoice) || isNil(chosen)) {
    return null;
  }

  const label = getAlternativeLabel(chosen);

  if (!customerChoice.isCurrent) {
    return (
      <Alert
        variant="warning"
        title={t(
          'entity.offerAlternative.choice.stale',
          '{label} · revision {revision} · the offer changed, the customer must confirm the choice again',
          {label, revision: customerChoice.revision}
        )}
        data-testid="offerAlternativeChoiceBanner"
      />
    );
  }

  const goToCheckout = () =>
    navigate(composePath(businessCaseRoutes.checkout, {params: {id: props.businessCaseId}}));

  return (
    <Alert
      variant="success"
      layout="detailed"
      title={
        customerChoice.chosenBy === 'SELLER'
          ? t(
              'entity.offerAlternative.choice.bySeller',
              '{label} · revision {revision} · selected by seller',
              {
                label,
                revision: customerChoice.revision,
              }
            )
          : t(
              'entity.offerAlternative.choice.byCustomer',
              '{label} · revision {revision} · selected by customer',
              {label, revision: customerChoice.revision}
            )
      }
      // Applied: go to checkout. Not yet: applying it is the way there.
      actions={buildArray<ButtonProps>().when(chosen.isApplied || chosen.canApply, {
        variant: 'link',
        title: t('entity.offerAlternative.actions.continueToCheckout', 'Continue to checkout'),
        rightIcon: 'navigation/chevron_right',
        isLoading: !chosen.isApplied && props.isApplying,
        onClick: chosen.isApplied ? goToCheckout : () => props.onApply(chosen, goToCheckout),
      })}
      data-testid="offerAlternativeChoiceBanner"
    />
  );
}
